// client/views/OntologyViewerV1.tsx
// Main Ontology Viewer for v1.2 OntologyBundle
// Implementation Plan v1.2 §6

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAsyncState } from '../hooks/useAsyncState';
import { OntologyGraph, type GraphNode as OntologyNode, type GraphEdge as OntologyEdge, type GraphBundle } from '../components/ontology-v1/OntologyGraph';
import { ComplianceBar, type PillarCompliance } from '../components/ontology-v1/ComplianceBar';
import { FAIRRuleCard, type FairRule } from '../components/ontology-v1/FAIRRuleCard';
import { Button } from '../components/primitives/Button';
import { OntologyChat } from '../components/OntologyChat';

interface ValidationFinding {
  ruleId: string;
  severity: "Info" | "Warning" | "Violation";
  message: string;
  path?: string;
}

interface OntologyBundle {
  version: string;
  namespace: string;
  nodes: OntologyNode[];
  edges: OntologyEdge[];
  rules: FairRule[];
  validation: {
    findings: ValidationFinding[];
    summaryByPillar: PillarCompliance[];
    validationSkipped?: boolean;
  };
  exports?: {
    owl?: string;
    ttl?: string;
    jsonld?: string;
    shacl?: string;
  };
  graphBundle?: GraphBundle; // Lightweight UI bundle (edgesfix.txt)
}

type PillarFilter = "All" | "F" | "A" | "I" | "R";

export function OntologyViewerV1() {
  const { job_id } = useParams<{ job_id: string }>();
  const { data, loading, error } = useAsyncState<OntologyBundle>(
    async () => {
      if (!job_id) return undefined;
      const res = await fetch(`/analysis/${job_id}/ontology-data`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      return res.json();
    },
    [job_id]
  );
  const [selectedNode, _setSelectedNode] = useState<OntologyNode | null>(null);
  const [pillarFilter, setPillarFilter] = useState<PillarFilter>("All");
  const [showChat, setShowChat] = useState(false);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div className="spinner" style={{ fontSize: "18px", color: "#6b7280" }}>
          Loading ontology...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "40px" }}>
        <h2 style={{ color: "#ef4444" }}>Error Loading Ontology</h2>
        <p style={{ color: "#6b7280" }}>{error}</p>
        <p style={{ fontSize: "14px", color: "#9ca3af", marginTop: "16px" }}>
          Make sure ENABLE_ONTOLOGY=true is set in .env
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <p style={{ color: "#6b7280" }}>No ontology data available for this job.</p>
      </div>
    );
  }

  const handleExport = (format: "ttl" | "jsonld" | "owl" | "shacl") => {
    window.location.href = `/analysis/${job_id}/ontology/export/${format}`;
  };

  return (
    <div className="ontology-viewer-v1" style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto", position: "relative" }}>
      {/* Header */}
      <header style={{ marginBottom: "32px", display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#111827", marginBottom: "8px" }}>
            FAIR Ontology View
          </h1>
          <p style={{ fontSize: "14px", color: "#6b7280" }}>
            Job ID: <span style={{ fontFamily: "monospace" }}>{job_id}</span>
            {" • "}
            Version: <span style={{ fontFamily: "monospace" }}>{data.version}</span>
            {data.graphBundle?.buildTimestamp && (
              <>
                {" • "}
                Build: <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {new Date(data.graphBundle.buildTimestamp).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
                  })}
                </span>
              </>
            )}
          </p>
        </div>
        <Button
          helpId="btn.askFairAI"
          onClick={() => setShowChat(!showChat)}
          style={{
            padding: "10px 20px",
            fontSize: "14px",
            fontWeight: "600",
            backgroundColor: showChat ? "#6b7280" : "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          {showChat ? "Close Chat" : "Ask FAIR AI"}
        </Button>
      </header>

      {data.validation.validationSkipped && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            backgroundColor: "#fef3c7",
            border: "1px solid #fbbf24",
            borderRadius: "6px",
            fontSize: "14px",
            color: "#92400e",
          }}
        >
          ⚠️ Validation skipped (ontology too large: {data.nodes.length} nodes)
        </div>
      )}

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <div style={{ fontSize: "14px", color: "#6b7280" }}>Classes</div>
          <div style={{ fontSize: "24px", fontWeight: "600", color: "#111827" }}>
            {data.nodes.filter((n) => n.type === "Class").length}
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <div style={{ fontSize: "14px", color: "#6b7280" }}>Properties</div>
          <div style={{ fontSize: "24px", fontWeight: "600", color: "#111827" }}>
            {data.nodes.filter((n) => n.type === "Property").length}
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <div style={{ fontSize: "14px", color: "#6b7280" }}>Individuals</div>
          <div style={{ fontSize: "24px", fontWeight: "600", color: "#111827" }}>
            {data.nodes.filter((n) => n.type === "Individual").length}
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <div style={{ fontSize: "14px", color: "#6b7280" }}>Edges</div>
          <div style={{ fontSize: "24px", fontWeight: "600", color: "#111827" }}>
            {data.graphBundle?.stats?.satisfied ?? data.edges.filter((e) => e.status === "satisfied").length} / {data.graphBundle?.stats?.totalEdges ?? data.edges.length}
          </div>
          <div style={{ fontSize: "11px", color: "#10b981" }}>satisfied</div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <div style={{ fontSize: "14px", color: "#6b7280" }}>FAIR Rules</div>
          <div style={{ fontSize: "24px", fontWeight: "600", color: "#111827" }}>
            {data.rules.length}
          </div>
        </div>
      </div>

      {/* Pillar Compliance */}
      <section style={{ marginBottom: "32px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#111827", marginBottom: "16px" }}>
          Pillar Compliance
        </h2>
        <div>
          {data.validation.summaryByPillar.map((pillar) => (
            <ComplianceBar key={pillar.pillar} compliance={pillar} />
          ))}
        </div>
      </section>

      {/* Graph Visualization */}
      <section style={{ marginBottom: "32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#111827" }}>
            Ontology Graph
          </h2>
          <div style={{ display: "flex", gap: "8px" }}>
            {(["All", "F", "A", "I", "R"] as PillarFilter[]).map((filter) => (
              <Button
                key={filter}
                helpId="filter.pillar"
                onClick={() => setPillarFilter(filter)}
                style={{
                  padding: "6px 12px",
                  fontSize: "14px",
                  fontWeight: pillarFilter === filter ? "600" : "400",
                  backgroundColor: pillarFilter === filter ? "#3b82f6" : "#f3f4f6",
                  color: pillarFilter === filter ? "#fff" : "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                {filter}
              </Button>
            ))}
          </div>
        </div>
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <OntologyGraph
            data={data.graphBundle || {
              nodes: data.nodes.map(n => ({ id: n.id, label: n.label, kind: (n.type || n.kind || 'Class') as 'Class' | 'Individual' | 'Property', pillar: n.pillar })),
              edges: data.edges.map(e => ({ ...e, id: `${e.source}|${e.label}|${e.target}` })),
              stats: { totalEdges: data.edges.length, satisfied: data.edges.filter((e: OntologyEdge) => e.status === 'satisfied').length }
            }}
            showMissing={true}
            pillarFilter={pillarFilter}
          />
        </div>
        {selectedNode && (
          <div
            style={{
              marginTop: "16px",
              padding: "16px",
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
          >
            <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "8px" }}>
              {selectedNode.label}
            </h3>
            <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>
              Type: {selectedNode.type}
            </p>
            {selectedNode.pillar && (
              <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>
                Pillar: {selectedNode.pillar}
              </p>
            )}
            {selectedNode.parent && (
              <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>
                Parent: {selectedNode.parent}
              </p>
            )}
          </div>
        )}
      </section>

      {/* FAIR Rules Catalog */}
      <section style={{ marginBottom: "32px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#111827", marginBottom: "16px" }}>
          FAIR Rules Catalog
        </h2>
        <div>
          {data.rules.map((rule) => {
            // Check if this rule passed based on validation findings
            const finding = data.validation.findings.find(f => f.ruleId === rule.id);
            const passed = finding?.severity === 'Info';
            return <FAIRRuleCard key={rule.id} rule={rule} passed={passed} />;
          })}
        </div>
      </section>

      {/* Export Buttons */}
      <section>
        <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#111827", marginBottom: "16px" }}>
          Export Ontology
        </h2>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <Button
            helpId="btn.exportTTL"
            onClick={() => handleExport("ttl")}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              backgroundColor: "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Download Turtle (TTL)
          </Button>
          <Button
            helpId="btn.exportJSONLD"
            onClick={() => handleExport("jsonld")}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              backgroundColor: "#10b981",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Download JSON-LD
          </Button>
          <Button
            helpId="btn.exportOWL"
            onClick={() => handleExport("owl")}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              backgroundColor: "#f59e0b",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Download OWL/XML
          </Button>
          <Button
            helpId="btn.exportSHACL"
            onClick={() => handleExport("shacl")}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              backgroundColor: "#8b5cf6",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Download SHACL Shapes
          </Button>
        </div>
      </section>

      {/* Chat Interface */}
      {showChat && job_id && (
        <div
          style={{
            position: "fixed",
            top: "0",
            right: "0",
            bottom: "0",
            width: "500px",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <OntologyChat
            jobId={job_id}
            onClose={() => setShowChat(false)}
          />
        </div>
      )}
    </div>
  );
}
